import { useState, useCallback, useRef } from 'react';
import { bookServiceApi } from '@/services/api';
import type { ReadingQuestion } from '../types/index';

export interface QuestionOption {
  option_id: number;
  text: string;
}

export interface Question {
  id: string;
  text: string;
  correct_option_id: number;
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  options: QuestionOption[];
  is_reviewed: boolean;
}

export interface QuestionsResponse {
  questions: Question[];
}

interface UseQuestionsState {
  questions: ReadingQuestion[];
  loading: boolean;
  error: string | null;
}

interface UseQuestionsReturn extends UseQuestionsState {
  fetchQuestions: (sectionId: number) => Promise<void>;
  clearQuestions: () => void;
}

// Convert API question format to ReadingQuestion format
const convertToReadingQuestion = (apiQuestion: Question): ReadingQuestion => {
  return {
    id: apiQuestion.id,
    question: apiQuestion.text,
    options: apiQuestion.options.map(option => option.text),
    correctAnswer: apiQuestion.correct_option_id - 1, // Convert to 0-based index
    explanation: apiQuestion.explanation,
    isReviewed: apiQuestion.is_reviewed,
    difficulty: apiQuestion.difficulty,
  };
};

export const useQuestions = (): UseQuestionsReturn => {
  const [state, setState] = useState<UseQuestionsState>({
    questions: [],
    loading: false,
    error: null,
  });
  
  // Track which sectionId we've already fetched to prevent duplicate requests
  const fetchedSectionIdRef = useRef<number | null>(null);
  const isFetchingRef = useRef(false);

  const fetchQuestions = useCallback(async (sectionId: number): Promise<void> => {
    if (!sectionId || !Number.isFinite(sectionId)) {
      setState(prev => ({ ...prev, error: 'Invalid section ID' }));
      return;
    }
    
    // Prevent duplicate requests for the same sectionId
    if (fetchedSectionIdRef.current === sectionId || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await bookServiceApi.get<QuestionsResponse>(`/questions/${sectionId}`);
      
      if (response.success && response.data) {
        const convertedQuestions = (response.data.questions || []).map(convertToReadingQuestion);
        
        fetchedSectionIdRef.current = sectionId;
        setState(prev => ({
          ...prev,
          questions: convertedQuestions,
          loading: false,
          error: null,
        }));
      } else {
        setState(prev => ({
          ...prev,
          questions: [],
          loading: false,
          error: response.error || 'خطا در دریافت سوالات',
        }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'خطای ناشناخته در دریافت سوالات';
      setState(prev => ({
        ...prev,
        questions: [],
        loading: false,
        error: errorMessage,
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const clearQuestions = (): void => {
    fetchedSectionIdRef.current = null;
    setState({
      questions: [],
      loading: false,
      error: null,
    });
  };

  return {
    ...state,
    fetchQuestions,
    clearQuestions,
  };
};
